import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { systemAPI, rbacAPI } from '../../services/api';
import { PERMISSION_MODULES } from '../../rbac';

const EMPTY_FORM = { name: '', email: '', password: '', role_id: '', phone: '', tenant_id: '' };

export default function SystemUsers() {
  const [users, setUsers]     = useState([]);
  const [tenants, setTenants] = useState([]);
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [page, setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ search: '', role: '', tenant_id: '' });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [userPermissions, setUserPermissions] = useState({});
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [resetModal, setResetModal] = useState(null);
  const [newPwd, setNewPwd]     = useState('');
  const [resetting, setResetting] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [sort, setSort] = useState({ key: 'full_name', direction: 'asc' });

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [uRes, tRes, rRes] = await Promise.all([
        systemAPI.getUsers({ page, limit: 15, ...filters }),
        systemAPI.getTenants({ limit: 200 }),
        rbacAPI.getRoles(),
      ]);
      const userData = uRes.data?.users || uRes.data?.data || [];
      setUsers(Array.isArray(userData) ? userData : []);
      const paging = uRes.data?.pagination || {};
      setTotalPages(paging.totalPages || Math.ceil((paging.total || 1) / 15) || 1);
      
      const tenantData = tRes.data?.tenants || tRes.data?.data || [];
      setTenants(Array.isArray(tenantData) ? tenantData : []);

      const roleData = rRes.data?.roles || [];
      setRoles(Array.isArray(roleData) ? roleData : []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load users');
    } finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1); };

  const selectedRole = roles.find(r => String(r.id) === String(form.role_id));
  const isPlatformRole = (roleName) => {
    const role = roles.find(r => r.name === roleName);
    return role ? ['system', 'taluka'].includes(role.portal_type) : false;
  };

  const openCreate = () => {
    setEditing(null); setForm(EMPTY_FORM); setUserPermissions({}); setSaveError(null); setShowPermissions(false); setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u.id);
    const roleId = u.role_id || (roles.find(r => r.name === u.role)?.id) || '';
    setForm({
      name: u.full_name || u.name || '',
      email: u.email || '',
      password: '',
      role_id: roleId ? String(roleId) : '',
      phone: u.phone || '',
      tenant_id: u.tenant_id || '',
    });
    setUserPermissions(u.permissions || {});
    setSaveError(null);
    setShowPermissions(false);
    setShowModal(true);
  };

  const handleRoleChange = (roleId) => {
    setForm(f => {
      const role = roles.find(r => String(r.id) === String(roleId));
      return {
        ...f,
        role_id: roleId,
        tenant_id: role && ['system', 'taluka'].includes(role.portal_type) ? '' : f.tenant_id,
      };
    });
    // Pre-fill permissions from role defaults
    const role = roles.find(r => String(r.id) === String(roleId));
    if (role?.default_permissions) {
      setUserPermissions({ ...role.default_permissions });
    } else {
      setUserPermissions({});
    }
  };

  const togglePermission = (module, action) => {
    setUserPermissions(prev => {
      const perms = { ...prev };
      const actions = [...(perms[module] || [])];
      const idx = actions.indexOf(action);
      if (idx >= 0) actions.splice(idx, 1); else actions.push(action);
      if (actions.length === 0) delete perms[module]; else perms[module] = actions;
      return perms;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true); setSaveError(null);
      const role = roles.find(r => String(r.id) === String(form.role_id));
      const payload = {
        full_name: form.name,
        email: form.email,
        phone: form.phone,
        role_id: form.role_id ? parseInt(form.role_id) : null,
        role: role?.name || 'citizen',
        tenant_id: form.tenant_id || null,
        permissions: userPermissions,
      };
      if (!editing) {
        payload.password = form.password;
        payload.username = form.email.split('@')[0];
      }
      if (editing && !payload.password) delete payload.password;
      if (!payload.tenant_id) payload.tenant_id = null;

      if (editing) await systemAPI.updateUser(editing, payload);
      else await systemAPI.createUser(payload);
      setShowModal(false);
      load();
    } catch (e) {
      setSaveError(e.response?.data?.error || e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    try {
      setResetting(true);
      await systemAPI.resetUserPassword(resetModal.id, { newPassword: newPwd });
      setResetModal(null);
      setNewPwd('');
    } catch (e) {
      alert(e.response?.data?.message || 'Reset failed');
    } finally { setResetting(false); }
  };

  const countPermissions = (perms) => {
    if (!perms || typeof perms !== 'object') return 0;
    return Object.values(perms).reduce((sum, actions) => sum + (Array.isArray(actions) ? actions.length : 0), 0);
  };

  const visibleUsers = useMemo(() => [...users].sort((a, b) => {
    const value = row => sort.key === 'tenant' ? row.Tenant?.name : sort.key === 'role' ? (row.roleRef?.display_name || row.role) : row[sort.key];
    return String(value(a) ?? '').localeCompare(String(value(b) ?? ''), 'mr', { numeric: true, sensitivity: 'base' }) * (sort.direction === 'asc' ? 1 : -1);
  }), [users, sort]);
  const SortHeader = ({ column, children }) => <button type="button" className={`sys-sort ${sort.key === column ? 'active' : ''}`} onClick={() => setSort(current => ({ key: column, direction: current.key === column && current.direction === 'asc' ? 'desc' : 'asc' }))}>{children}<span>{sort.key === column ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span></button>;
  const exportCsv = () => { const q = value => `"${String(value ?? '').replace(/"/g, '""')}"`; const rows = [['Name','Email','Role','Portal','Gram Panchayat','Permissions','Status'], ...visibleUsers.map(u => [u.full_name || u.name,u.email,u.roleRef?.display_name || u.role,u.roleRef?.portal_type,u.Tenant?.name,countPermissions(u.permissions),u.is_active !== false ? 'Active' : 'Inactive'])]; const link = document.createElement('a'); link.href = URL.createObjectURL(new Blob(['\ufeff', rows.map(row => row.map(q).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' })); link.download = 'system-users.csv'; link.click(); URL.revokeObjectURL(link.href); };

  return (
    <div className="sys-page">
      <div className="sys-page-header">
        <div>
          <h1>System Users</h1>
          <p>Manage all users across the platform</p>
        </div>
        <button className="sys-btn sys-btn-primary" onClick={openCreate}>+ Add User</button>
      </div>

      <div className="sys-filter-bar sys-toolbar">
        <input placeholder="Search name, email…" value={filters.search} onChange={e => setF('search', e.target.value)} />
        <select value={filters.role} onChange={e => setF('role', e.target.value)}>
          <option value="">All Roles</option>
          {roles.map(r => <option key={r.id} value={r.name}>{r.display_name}</option>)}
        </select>
        <select value={filters.tenant_id} onChange={e => setF('tenant_id', e.target.value)}>
          <option value="">All GPs</option>
          <option value="system">Platform Users</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button className="sys-btn sys-btn-outline" onClick={exportCsv}>↓ Export CSV</button>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}>Loading…</div>}
      {error   && <div style={{ textAlign: 'center', padding: 40, color: '#c62828' }}>{error}</div>}

      {!loading && !error && (
        <>
          <div className="sys-card">
            <div className="sys-table-wrap">
              <table className="sys-table">
                <thead>
                  <tr><th><SortHeader column="full_name">Name</SortHeader></th><th><SortHeader column="email">Email</SortHeader></th><th><SortHeader column="role">Role</SortHeader></th><th>Portal</th><th><SortHeader column="tenant">Gram Panchayat</SortHeader></th><th>Permissions</th><th><SortHeader column="is_active">Status</SortHeader></th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {visibleUsers.map((u, i) => (
                    <tr key={u.id || i}>
                      <td><strong>{u.full_name || u.name || '—'}</strong></td>
                      <td>{u.email}</td>
                      <td>
                        <span style={{ textTransform: 'capitalize' }}>{u.roleRef?.display_name || u.role?.replace(/_/g, ' ')}</span>
                      </td>
                      <td>
                        <span style={{
                          background: { system: '#c0392b', taluka: '#d35400', admin: '#8e44ad', citizen: '#2980b9', public: '#27ae60' }[u.roleRef?.portal_type] || '#888',
                          color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600
                        }}>
                          {u.roleRef?.portal_type || '—'}
                        </span>
                      </td>
                      <td>{isPlatformRole(u.role) ? <em style={{ color: '#888' }}>Platform</em> : (u.Tenant?.name || u.tenant_id || '—')}</td>
                      <td style={{ fontSize: '0.8rem', color: '#666' }}>{countPermissions(u.permissions)} perms</td>
                      <td><span className={`sys-badge ${u.is_active !== false ? 'sys-badge-active' : 'sys-badge-inactive'}`}>{u.is_active !== false ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="sys-btn sys-btn-outline" style={{ padding: '4px 12px', fontSize: '0.78rem' }} onClick={() => openEdit(u)}>Edit</button>
                          <button className="sys-btn sys-btn-outline" style={{ padding: '4px 12px', fontSize: '0.78rem' }} onClick={() => { setResetModal(u); setNewPwd(''); }}>Reset Pwd</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#aaa' }}>No users found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="sys-pagination">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}>← Prev</button>
            <span style={{ fontSize: '0.85rem', color: '#555' }}>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>Next →</button>
          </div>
        </>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="sys-modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="sys-modal" style={{ maxWidth: 800, maxHeight: '90vh', overflow: 'auto' }}>
            <h2>{editing ? 'Edit User' : 'Add User'}</h2>
            {saveError && <div style={{ color: '#c62828', marginBottom: 12, fontSize: '0.85rem' }}>{saveError}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="sys-form-group">
                <label>Full Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="sys-form-group">
                <label>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
            </div>

            {!editing && (
              <div className="sys-form-group">
                <label>Password *</label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="sys-form-group">
                <label>Role *</label>
                <select value={form.role_id} onChange={e => handleRoleChange(e.target.value)}>
                  <option value="">— Select Role —</option>
                  {roles.filter(r => r.is_active).map(r => (
                    <option key={r.id} value={r.id}>{r.display_name} ({r.portal_type})</option>
                  ))}
                </select>
              </div>
              <div className="sys-form-group">
                <label>Grampanchayat {selectedRole && !['system', 'taluka'].includes(selectedRole.portal_type) ? '*' : ''}</label>
                <select
                  value={form.tenant_id}
                  onChange={e => setForm(f => ({ ...f, tenant_id: e.target.value }))}
                  disabled={selectedRole && ['system', 'taluka'].includes(selectedRole.portal_type)}
                >
                  <option value="">{selectedRole && ['system', 'taluka'].includes(selectedRole.portal_type) ? 'N/A (Platform role)' : '— Select GP —'}</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>

            <div className="sys-form-group">
              <label>Phone</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </div>

            {/* Permissions Section */}
            {form.role_id && (
              <div style={{ marginTop: 16, border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
                <div
                  style={{ padding: '10px 16px', background: '#f5f5f5', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onClick={() => setShowPermissions(!showPermissions)}>
                  <div>
                    <strong>User Permissions</strong>
                    <span style={{ marginLeft: 8, fontSize: '0.8rem', color: '#666' }}>
                      ({countPermissions(userPermissions)} selected)
                    </span>
                  </div>
                  <span style={{ fontSize: '1.2rem' }}>{showPermissions ? '▲' : '▼'}</span>
                </div>

                {showPermissions && (
                  <div style={{ padding: 16 }}>
                    <p style={{ fontSize: '0.82rem', color: '#666', marginTop: 0 }}>
                      Permissions are pre-filled from the role defaults. Customize per-user as needed.
                    </p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: '#f9f9f9' }}>
                          <th style={{ padding: '6px 12px', textAlign: 'left' }}>Module</th>
                          <th style={{ padding: '6px 6px', textAlign: 'center', width: 55 }}>View</th>
                          <th style={{ padding: '6px 6px', textAlign: 'center', width: 55 }}>Create</th>
                          <th style={{ padding: '6px 6px', textAlign: 'center', width: 55 }}>Update</th>
                          <th style={{ padding: '6px 6px', textAlign: 'center', width: 55 }}>Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        {PERMISSION_MODULES.map((mod, i) => {
                          const currentPerms = userPermissions[mod.module] || [];
                          return (
                            <tr key={mod.module} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ padding: '5px 12px', fontWeight: 500 }}>{mod.label}</td>
                              {['view', 'create', 'update', 'delete'].map(action => (
                                <td key={action} style={{ textAlign: 'center' }}>
                                  {mod.actions.includes(action) ? (
                                    <input type="checkbox"
                                      checked={currentPerms.includes(action)}
                                      onChange={() => togglePermission(mod.module, action)}
                                      style={{ cursor: 'pointer', width: 15, height: 15 }} />
                                  ) : <span style={{ color: '#ccc' }}>—</span>}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="sys-form-actions" style={{ marginTop: 16 }}>
              <button className="sys-btn sys-btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="sys-btn sys-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : (editing ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetModal && (
        <div className="sys-modal-overlay" onClick={e => e.target === e.currentTarget && setResetModal(null)}>
          <div className="sys-modal" style={{ maxWidth: 420 }}>
            <h2>Reset Password</h2>
            <p style={{ color: '#555', marginBottom: 12 }}>Set a new password for <strong>{resetModal.full_name || resetModal.name || resetModal.email}</strong>.</p>
            <div className="sys-form-group">
              <label>New Password *</label>
              <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="Min 8 characters" />
            </div>
            <div className="sys-form-actions">
              <button className="sys-btn sys-btn-outline" onClick={() => setResetModal(null)}>Cancel</button>
              <button className="sys-btn sys-btn-primary" onClick={handleReset} disabled={resetting || newPwd.length < 8}>
                {resetting ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
